/* eslint-disable @typescript-eslint/ban-types */
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  RadioGroup,
} from '@material-ui/core'
import { renderHelpersText, renderErrorInField } from '@flatsy/utils/Functions/form/Helpers'
import React, { useEffect } from 'react'
import clsx from 'clsx'
import { PrimaryRadio } from '../../../../../../MaterialUi/Radio'
import { FieldsType, SelectType, SingleFieldProps } from '../../../../Types'
import { useStyle } from './Style'

const RadioButtonField = <T extends {}>({
  field,
  input,
  meta,
  values,
}: SingleFieldProps<T>): React.ReactElement => {
  const classes = useStyle()
  /**
   * @description render label or condition label
   * @param field
   * @param values
   */
  const renderLabelInField = (field: FieldsType, values: T) => {
    return field.conditional ? field.conditional(values).label : field.label
  }

  /**
   * @description format field value
   * @param val
   */
  const onChange = (val: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (field.changeEvent) {
      field.changeEvent(val.target.value)
    }
    input.onChange(val.target.value)
  }

  useEffect(() => {
    if (field.selectInitialValue && !input.value) {
      input.onChange(field.selectInitialValue)
    }
  }, [field.selectInitialValue])

  /**
   * @description render an array of react element
   * @param options
   */
  const renderOptions = (options: SelectType[] | undefined) => {
    if (options) {
      return options.map((select) => (
        <FormControlLabel
          value={select.value}
          key={`radioButton-${field.name}-${select.value}`}
          control={<PrimaryRadio key={`radioControl-${select.value}`} />}
          label={select.label}
          className={clsx(
            classes.item,
            input.value === select.value ? classes.itemActive : undefined
          )}
          name={`${field.name}-${select.value}`}
          data-cy={`radio-${field.name}-${select.value}`}
        />
      ))
    }
    return <div />
  }

  return (
    <FormControl
      component="fieldset"
      error={renderErrorInField(meta)}
      name={field.name}
      key={field.name}
      className={classes.formGroup}
    >
      {field.label && field.label !== '' && (
        <FormLabel component="legend">{renderLabelInField(field, values)}</FormLabel>
      )}
      <RadioGroup
        aria-label={field.name}
        name={field.name}
        value={input?.value || field.selectInitialValue}
        onChange={onChange}
        className={classes.group}
      >
        {renderOptions(field.selectValue)}
      </RadioGroup>
      <FormHelperText>
        {renderHelpersText(field, meta.touched, meta.error, meta.submitError)}
      </FormHelperText>
    </FormControl>
  )
}

export default RadioButtonField
